// Single source of truth for the textarea-plus-send affordance.
// Used by the main chat's Composer and the right-drawer's
// SimpleChat so both composers render identically (including the
// focus-within ring and the auto-growing row count). Previously
// both declared their own inline textarea/button and drifted —
// Captain + Reef audit 2026-04-24 flagged the duplication.

import type {
  FocusEventHandler,
  KeyboardEventHandler,
  Ref,
} from "react";
import { Play } from "lucide-react";

export function ChatInput({
  value,
  onChange,
  onKeyDown,
  onBlur,
  inputRef,
  placeholder,
  busy,
  testId = "chat-input",
  autoComplete = "off",
}: {
  value: string;
  /** Fires on every keystroke. `caret` is the textarea's
   * `selectionStart` at the moment of change — main chat uses it
   * to drive mention autocomplete; drawer chat ignores the arg. */
  onChange: (value: string, caret: number) => void;
  onKeyDown?: KeyboardEventHandler<HTMLTextAreaElement>;
  onBlur?: FocusEventHandler<HTMLTextAreaElement>;
  inputRef?: Ref<HTMLTextAreaElement>;
  placeholder: string;
  busy: boolean;
  testId?: string;
  autoComplete?: string;
}) {
  // Grow with content up to 8 rows, floor at 2 so the send button
  // always has a sensible target.
  const rows = Math.min(Math.max(value.split("\n").length, 2), 8);

  return (
    <div className="flex items-end gap-2 rounded-md border border-edge bg-surface-raised px-2 py-1 focus-within:border-border-highlight">
      <textarea
        ref={inputRef}
        value={value}
        onChange={(e) => {
          const next = e.target.value;
          const caret = e.target.selectionStart ?? next.length;
          onChange(next, caret);
        }}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        placeholder={placeholder}
        rows={rows}
        className="min-h-[2.75rem] flex-1 resize-none bg-transparent px-1 py-0.5 text-xs text-primary outline-none placeholder:text-faint"
        data-testid={testId}
        autoComplete={autoComplete}
      />
      <button
        type="submit"
        disabled={busy || value.trim() === ""}
        aria-label="Send"
        title="Send"
        className="flex h-5 w-5 cursor-pointer items-center justify-center rounded-md text-muted transition-colors hover:bg-surface hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-muted"
      >
        <Play size={12} fill="currentColor" strokeWidth={0} />
      </button>
    </div>
  );
}
